import React, { useState, useEffect } from 'react';
import AppDownload from './AppDownload';
import { X, LogOut, Search, ChevronRight, ChevronDown, ChevronLeft, Star, RefreshCw, LayoutDashboard, Users, BarChart2, Router, Ticket, Settings } from 'lucide-react';

// The installed app has no browser refresh button and pull-to-refresh is off in App View.
// Clears the cached copy and reloads, so a newly deployed build is picked up.
async function refreshApp() {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) || [];
    await Promise.all(regs.map((r) => r.unregister()));
    const keys = (await window.caches?.keys()) || [];
    await Promise.all(keys.map((k) => window.caches.delete(k)));
  } catch { /* reload anyway */ }
  window.location.reload();
}

// The admin app view's "More" sheet: Refresh / Sign out / Close icons on top, search, pinned + recent screens,
// then the six desktop categories (utils/appMenu.js) as tiles. Tapping a tile opens that category's list;
// group rows (Customers, Payments…) expand in place. Stars to pin a screen only appear after "Edit pins".
const ROW_H = 52;
const rowBase = {
  display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: ROW_H, padding: '0 14px',
  background: 'none', border: 0, cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit',
  fontSize: '.92rem', fontWeight: 500, textAlign: 'left',
};
const listCard = { background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' };
const iconBox = { width: 32, height: 32, borderRadius: 8, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--primary)' };
const roundBtn = { background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' };
const CATEGORY_ICONS = { Daily: LayoutDashboard, 'Customer & Billing': Users, Reports: BarChart2, Network: Router, GTPL: Ticket, Admin: Settings };

function Row({ entry, first, pinned, onOpen, onPin, editing, expanded, onExpand, nested }) {
  const isGroup = !!entry.children;
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderTop: first ? 0 : '1px solid var(--border)', background: nested ? 'var(--bg)' : undefined }}>
      <button type="button" onClick={() => (isGroup ? onExpand(entry.id) : onOpen(entry))}
        style={{ ...rowBase, flex: 1, minWidth: 0, paddingLeft: nested ? 28 : 14 }}>
        {!nested && <span style={iconBox}><entry.icon size={17} /></span>}
        <span style={{ flex: 1, minWidth: 0, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{entry.label}</span>
        {isGroup
          ? <ChevronDown size={17} color="var(--text-muted)" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          : !editing && <ChevronRight size={16} color="var(--text-muted)" />}
      </button>
      {editing && !isGroup && onPin && (
        <button type="button" aria-label={pinned ? `Unpin ${entry.label}` : `Pin ${entry.label}`} onClick={() => onPin(entry.id)}
          style={{ width: 48, height: ROW_H, background: 'none', border: 0, cursor: 'pointer', color: pinned ? 'var(--cb-f59e0b, #f59e0b)' : 'var(--text-muted)' }}>
          <Star size={18} fill={pinned ? 'currentColor' : 'none'} />
        </button>
      )}
    </div>
  );
}

// The part of the screen that is actually visible. iPhone Safari does not shrink the page when the keyboard opens,
// it covers the bottom of it — a sheet pinned to the bottom then has its search results hidden behind the keyboard.
// visualViewport is the area above the keyboard; the sheet is sized to that instead.
function useVisibleArea() {
  const read = () => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    return vv ? { top: vv.offsetTop, height: vv.height } : { top: 0, height: typeof window !== 'undefined' ? window.innerHeight : 800 };
  };
  const [area, setArea] = useState(read);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setArea(read());
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, []);
  return area;
}

const Heading = ({ children, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px 6px', fontSize: '.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
    <span>{children}</span>{right}
  </div>
);

export default function AppMoreSheet({ sections, leaves, pins, recent, onTogglePin, onOpen, onClose, onLogout }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [category, setCategory] = useState(null); // title of the open category, null = the six-tile home
  const [showRare, setShowRare] = useState(false);
  const [editing, setEditing] = useState(false);
  const byId = new Map(leaves.map((l) => [l.id, l]));
  const pinned = pins.map((id) => byId.get(id)).filter(Boolean);
  const recents = recent.map((id) => byId.get(id)).filter((l) => l && !pins.includes(l.id));
  const q = query.trim().toLowerCase();
  const results = q ? leaves.filter((l) => l.label.toLowerCase().includes(q)) : null;
  const toggleExpand = (id) => setExpanded((e) => (e === id ? null : id));
  const area = useVisibleArea();
  const kb = typeof window !== 'undefined' && window.innerHeight - area.height > 150;
  const barH = '(58px + env(safe-area-inset-bottom, 0px))';
  const open = sections.find((s) => s.title === category);

  const renderList = (entries, topBorder) => entries.map((entry, i) => (
    <React.Fragment key={entry.id}>
      <Row entry={entry} first={i === 0 && !topBorder} pinned={pins.includes(entry.id)} onOpen={onOpen} onPin={onTogglePin}
        editing={editing} expanded={expanded === entry.id} onExpand={toggleExpand} />
      {entry.children && expanded === entry.id && entry.children.map((c) => (
        <Row key={c.id} nested entry={c} pinned={pins.includes(c.id)} onOpen={onOpen} onPin={onTogglePin} editing={editing} />
      ))}
    </React.Fragment>
  ));
  const editBtn = (
    <button type="button" onClick={() => setEditing((e) => !e)}
      style={{ background: 'none', border: 0, color: 'var(--primary)', fontSize: '.8rem', fontWeight: 600, padding: '8px 0 8px 12px', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
      {editing ? 'Done' : 'Edit pins'}
    </button>
  );
  const rareEntries = open ? open.entries.filter((e) => e.rare) : [];

  return (
    // The tab bar stays visible under the sheet; with the keyboard up the bar is hidden behind it, so use the full area.
    <div onClick={onClose} style={{ position: 'fixed', left: 0, right: 0, top: area.top, height: kb ? area.height : `calc(${area.height}px - ${barH})`, background: 'rgba(0,0,0,.5)', zIndex: 100 }}>
      <div
        data-sheet
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: kb ? `${Math.round(area.height * 0.92)}px` : `calc(${Math.round(area.height * 0.92)}px - ${barH})`, overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain', background: 'var(--bg)', borderRadius: '18px 18px 0 0', padding: `0 14px ${kb ? 'calc(24px + env(safe-area-inset-bottom, 0px))' : '24px'}` }}
      >
        <div style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 1, padding: '14px 0 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: '1.05rem', flex: 1 }}>Menu</span>
            <button type="button" onClick={refreshApp} title="Refresh app" aria-label="Refresh app" style={{ ...roundBtn, color: 'var(--info, #2563eb)' }}><RefreshCw size={18} /></button>
            <button type="button" onClick={onLogout} title="Sign out" aria-label="Sign out"
              style={{ ...roundBtn, color: 'var(--danger, #dc2626)', marginRight: 10 }}><LogOut size={18} /></button>
            <button type="button" data-sheet-close onClick={onClose} title="Close menu" aria-label="Close menu" style={roundBtn}><X size={19} /></button>
          </div>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              className="search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a screen…"
              style={{ width: '100%', paddingLeft: 38, height: 44, borderRadius: 10 }}
            />
          </div>
        </div>

        {results ? (
          results.length ? <div style={{ marginTop: 6, ...listCard }}>{renderList(results)}</div>
            : <div style={{ color: 'var(--text-muted)', fontSize: '.88rem', padding: '20px 4px' }}>No screen matches "{query}".</div>
        ) : open ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <button type="button" onClick={() => { setCategory(null); setExpanded(null); setShowRare(false); }}
                style={{ ...rowBase, width: 'auto', minHeight: 48, padding: '0 6px 0 0', fontWeight: 700, fontSize: '1rem' }}>
                <ChevronLeft size={22} /> {open.title}
              </button>
              {editBtn}
            </div>
            <div style={listCard}>
              {renderList(open.entries.filter((e) => !e.rare))}
              {rareEntries.length > 0 && (
                <>
                  <button type="button" onClick={() => setShowRare((s) => !s)}
                    style={{ ...rowBase, borderTop: '1px solid var(--border)', color: 'var(--primary)', fontWeight: 600, fontSize: '.86rem', justifyContent: 'center' }}>
                    {showRare ? 'Show fewer' : `Show ${rareEntries.length} more`}
                  </button>
                  {showRare && renderList(rareEntries, true)}
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {(pinned.length > 0 || recents.length > 0 || editing) && (
              <>
                <Heading right={editBtn}>Quick access</Heading>
                {pinned.length + recents.length > 0 ? <div style={listCard}>{renderList([...pinned, ...recents])}</div>
                  : <div style={{ color: 'var(--text-muted)', fontSize: '.82rem', padding: '0 4px' }}>No pins yet. Open a category and tap ☆ on any screen.</div>}
              </>
            )}
            <Heading right={pinned.length === 0 && recents.length === 0 && !editing ? editBtn : null}>Categories</Heading>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              {sections.map((s) => {
                const Icon = CATEGORY_ICONS[s.title] || LayoutDashboard;
                return (
                  <button key={s.title} type="button" onClick={() => setCategory(s.title)}
                    style={{ ...listCard, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10, minHeight: 88, padding: 14, cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit', textAlign: 'left' }}>
                    <span style={iconBox}><Icon size={18} /></span>
                    <span style={{ fontWeight: 600, fontSize: '.9rem', lineHeight: 1.25 }}>{s.title}</span>
                  </button>
                );
              })}
            </div>
            <AppDownload />
          </>
        )}
      </div>
    </div>
  );
}
